import { Button } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import t from 'react-translate';

import NvRadioButton from 'shared/components/inputs/nv-radio-button';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import NvTooltip, { TextAlign, TriggerType } from 'shared/components/nv-tooltip';
import { InsightVisibility } from 'redux/schemas/models/exercise';

import { bodyStyles } from './ai-insights-setup-modal-styles';

const MIN_CUSTOM_CONTEXT_WORDS = 300;

const countWords = (text: string): number => {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

export type FormValues = {
  insightVisibility: string;
  // Serialized as 'true' | 'false' since radio inputs require string values;
  // converted to boolean at the save boundary.
  useCustomContext: 'true' | 'false';
  customContext: string;
};

export type AiInsightsSetupModalBodyProps = {
  onClose: () => void;
  onSave: (
    insightVisibility: InsightVisibility,
    useCustomContext: boolean,
    customContext: string
  ) => void;
  currentVisibility: InsightVisibility;
  currentUseCustomContext: boolean;
  currentCustomContext: string;
  courseAliases: Record<string, string>;
  isCollection: boolean;
  isLinked: boolean;
};

const AiInsightsSetupModalBody = ({
  onClose,
  onSave,
  courseAliases,
  currentVisibility,
  currentUseCustomContext,
  currentCustomContext,
  isCollection,
  isLinked,
}: AiInsightsSetupModalBodyProps) => {
  const translations = t.LECTURE_PAGES.COMPONENTS.AI_INSIGHTS;
  const { control } = useFormContext<FormValues>();
  const selectedVisibility = useWatch({ control, name: 'insightVisibility' }) as InsightVisibility;
  const selectedUseCustomContext = useWatch({ control, name: 'useCustomContext' }) as 'true' | 'false';
  const selectedCustomContext = useWatch({ control, name: 'customContext' }) ?? '';

  const handleRadioGroupKeyDown = <T extends string>(
    e: React.KeyboardEvent<HTMLDivElement>,
    options: ReadonlyArray<{ value: T }>,
    currentValue: T,
    fieldName: keyof FormValues
  ) => {
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key))
      return;
    e.preventDefault();
    e.stopPropagation();
    const currentIdx = options.findIndex((o) => o.value === currentValue);
    const baseIdx = currentIdx === -1 ? 0 : currentIdx;
    const nextIdx = ['ArrowDown', 'ArrowRight'].includes(e.key)
      ? (baseIdx + 1) % options.length
      : (baseIdx - 1 + options.length) % options.length;
    const nextValue = options[nextIdx].value;
    const nextInput = document.getElementById(
      `${fieldName as string}-${nextValue}`
    ) as HTMLInputElement | null;
    if (!nextInput) return;
    nextInput.focus();
    if (!nextInput.checked) nextInput.click();
  };

  const visibilityOptions = [
    {
      value: InsightVisibility.DISABLED,
      label: translations.OPTION_DISABLED(),
      dataQa: 'ai-insights-option-disabled',
    },
    {
      value: InsightVisibility.LEARNERS,
      label: translations.OPTION_DIRECT(courseAliases),
      dataQa: 'ai-insights-option-learners',
    },
    {
      value: InsightVisibility.ADMINS,
      label: translations.OPTION_ADMINS(courseAliases),
      dataQa: 'ai-insights-option-admins',
    },
  ];

  const useCustomContextOptions = [
    {
      value: 'false' as const,
      label: translations.USE_COURSE_CONTENT(),
      description: translations.USE_COURSE_CONTENT_DESCRIPTION(),
      dataQa: 'AI_INSIGHTS_OPTION_USE_COURSE_CONTENT',
    },
    {
      value: 'true' as const,
      label: translations.USE_CUSTOM_CONTEXT(),
      description: translations.USE_CUSTOM_CONTEXT_DESCRIPTION(),
      dataQa: 'AI_INSIGHTS_OPTION_USE_CUSTOM_CONTEXT',
    },
  ];

  const isContentOptionsOpen =
    selectedVisibility !== InsightVisibility.DISABLED;
  const isCustomContextSelected = selectedUseCustomContext === 'true';
  const customContextMeetsMinimum = countWords(selectedCustomContext) >= MIN_CUSTOM_CONTEXT_WORDS;


  const hasChanges = isLinked
    ? selectedVisibility !== currentVisibility
    : selectedVisibility !== currentVisibility ||
      isCustomContextSelected !== currentUseCustomContext ||
      (selectedCustomContext ?? '') !== (currentCustomContext ?? '');

  const requiresValidCustomContext =
    !isLinked && isContentOptionsOpen && isCustomContextSelected;
  const saveDisabled =
    !selectedVisibility ||
    !hasChanges ||
    (requiresValidCustomContext && !customContextMeetsMinimum);

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) {
      e.stopPropagation();
    }
  };

  return (
    <div
      css={bodyStyles(isCollection, isLinked)}
      className="d-flex align-items-center flex-column w-100"
      data-qa="ai-insights-setup-modal"
    >
      <p className="text-regular mt-5 mb-4">
        {translations.MODAL_DESCRIPTION()}
      </p>
      <div
        className="selection d-flex flex-column justify-content-center"
        role="radiogroup"
        aria-label={translations.MODAL_TITLE()}
        onKeyDown={(e) =>
          handleRadioGroupKeyDown(
            e,
            visibilityOptions,
            selectedVisibility,
            'insightVisibility'
          )
        }
      >
        {visibilityOptions.map((option) => (
          <NvTooltip
            key={option.value}
            text={t.LECTURE_PAGES.COMPONENTS.DROPDOWN.SETUP_IN_LINKED_LESSON_TOOLTIP()}
            placement="top"
            triggerType={TriggerType.HOVER}
            enabled={isCollection}
            textAlign={TextAlign.CENTER}
          >
            <div
              className={`option${
                selectedVisibility === option.value ? ' selected' : ''
              }`}
            >
              <NvRadioButton
                withForm
                disabled={isCollection}
                value={option.value}
                name="insightVisibility"
                label={option.label}
                ariaLabel={option.label}
                data-qa={option.dataQa}
              />
            </div>
          </NvTooltip>
        ))}
      </div>
      <div
        className={`content-options${isContentOptionsOpen ? ' open' : ''}`}
        data-qa="AI_INSIGHTS_CONTENT_OPTIONS_CONTAINER"
        aria-hidden={!isContentOptionsOpen}
      >
        <div className="content-options-inner">
          <p className="content-options-title">
            {translations.CONTENT_OPTIONS_TITLE()}
          </p>
          <div
            className="d-flex flex-column"
            role="radiogroup"
            aria-label={translations.CONTENT_OPTIONS_TITLE()}
            onKeyDown={(e) =>
              handleRadioGroupKeyDown(
                e,
                useCustomContextOptions,
                selectedUseCustomContext,
                'useCustomContext'
              )
            }
          >
            {useCustomContextOptions.map((option) => {
              const isSelected = selectedUseCustomContext === option.value;
              const isCustomCard = option.value === 'true';
              return (
                <div
                  key={option.value}
                  className={`content-option${isSelected ? ' selected' : ''}`}
                >
                  <NvTooltip
                    text={translations.MODAL_CONTENT_LINKED_TOOLTIP()}
                    placement="top"
                    triggerType={TriggerType.HOVER}
                    enabled={isLinked}
                    textAlign={TextAlign.CENTER}
                  >
                    <div>
                      <NvRadioButton
                        withForm
                        disabled={isLinked}
                        value={option.value}
                        name="useCustomContext"
                        label={option.label}
                        ariaLabel={option.label}
                        data-qa={option.dataQa}
                      />
                    </div>
                  </NvTooltip>
                  <p className="description">{option.description}</p>
                  {isCustomCard && (
                    <div
                      className={`textarea-reveal${isSelected ? ' open' : ''}`}
                      aria-hidden={!isSelected}
                    >
                      <NvTextArea
                        withForm
                        required
                        readOnly={isLinked}
                        name="customContext"
                        placeholder={translations.CUSTOM_CONTEXT_PLACEHOLDER()}
                        ariaLabel={translations.USE_CUSTOM_CONTEXT()}
                        data-qa="AI_INSIGHTS_CUSTOM_CONTEXT_TEXTAREA"
                        onKeyDown={handleTextareaKeyDown}
                      />
                      <p
                        className="word-count"
                        data-qa="AI_INSIGHTS_CUSTOM_CONTEXT_WORD_COUNT"
                      >
                        <span
                          className={`count${customContextMeetsMinimum ? ' met' : ''}`}
                        >
                          {translations.CUSTOM_CONTEXT_WORD_COUNT(countWords(selectedCustomContext))}
                        </span>
                        {' '}
                        <span className='minimum'>
                          {translations.CUSTOM_CONTEXT_WORD_COUNT_MINIMUM()}
                        </span>
                        {' '}
                        <span className={`icon icon-xss-smallest ${customContextMeetsMinimum ? 'icon-success met' : 'icon-warning'}`} />
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="buttons-row mt-6 mb-5">
        <Button
          variant="secondary"
          className="mr-2"
          onClick={onClose}
          aria-label={t.FORM.CANCEL()}
          data-qa="ai-insights-setup-cancel"
        >
          {t.FORM.CANCEL()}
        </Button>
        <Button
          variant="primary"
          onClick={() =>
            onSave(
              selectedVisibility,
              isCustomContextSelected,
              selectedCustomContext
            )
          }
          aria-label={t.FORM.SAVE()}
          data-qa="ai-insights-setup-save"
          disabled={saveDisabled}
        >
          {t.FORM.SAVE()}
        </Button>
      </div>
    </div>
  );
};

export default AiInsightsSetupModalBody;
